import {Injectable} from "@angular/core";
import {BehaviorSubject} from "rxjs";
import type {WorksModel} from "src/app/models/WorksModel";
import type {
  WorkSelectionItem,
  WorkSelectionReference,
  WorkSelectionToggle,
} from "../interfaces/work-selection.interface";

@Injectable()
export class WorkSelectionService {
  private readonly selected = new Map<string, WorkSelectionItem>();
  private anchorKey?: string;
  private readonly selectionSubject = new BehaviorSubject<WorkSelectionItem[]>(
    []
  );
  private readonly selectionModeSubject = new BehaviorSubject(false);
  private readonly busySubject = new BehaviorSubject(false);

  readonly selection$ = this.selectionSubject.asObservable();
  readonly selectionMode$ = this.selectionModeSubject.asObservable();
  readonly busy$ = this.busySubject.asObservable();

  get selection(): WorkSelectionItem[] {
    return [...this.selected.values()];
  }

  get isSelectionMode(): boolean {
    return this.selectionModeSubject.value;
  }

  get isBusy(): boolean {
    return this.busySubject.value;
  }

  enter(): void {
    this.selectionModeSubject.next(true);
  }

  toggle({work, range}: WorkSelectionToggle, works: WorksModel[] = []): void {
    if (this.isBusy) return;
    if (range && this.selectRange(work, works)) return;
    const item = this.toSelectionItem(work);
    if (this.selected.has(item.key)) {
      this.selected.delete(item.key);
    } else {
      this.selected.set(item.key, item);
    }
    this.anchorKey = item.key;
    this.publish();
  }

  toggleAll(works: WorksModel[]): void {
    if (this.isBusy || !works.length) return;
    const allSelected = works.every((work) => this.isSelected(work));
    for (const work of works) {
      const item = this.toSelectionItem(work);
      if (allSelected) this.selected.delete(item.key);
      else this.selected.set(item.key, item);
    }
    this.anchorKey = undefined;
    this.publish();
  }

  private selectRange(work: WorksModel, works: WorksModel[]): boolean {
    const start = works.findIndex(
      (item) => this.getKey(item) === this.anchorKey
    );
    const end = works.findIndex(
      (item) => this.getKey(item) === this.getKey(work)
    );
    if (start < 0 || end < 0) return false;
    for (const item of works.slice(
      Math.min(start, end),
      Math.max(start, end) + 1
    )) {
      const selected = this.toSelectionItem(item);
      this.selected.set(selected.key, selected);
    }
    this.publish();
    return true;
  }

  isSelected(work: WorksModel): boolean {
    return this.selected.has(this.getKey(work));
  }

  removeSuccesses(references: WorkSelectionReference[]): void {
    for (const reference of references) {
      this.selected.delete(`${reference.type}:${reference._id}`);
    }
    this.publish();
  }

  updateWorks(works: WorksModel[]): void {
    let changed = false;
    for (const work of works) {
      const key = this.getKey(work);
      const current = this.selected.get(key);
      if (!current || current.work === work) {
        continue;
      }
      this.selected.set(key, {...current, work});
      changed = true;
    }
    if (changed) {
      this.publish();
    }
  }

  reconcile(works: WorksModel[]): void {
    const availableKeys = new Set(works.map((work) => this.getKey(work)));
    if (this.anchorKey && !availableKeys.has(this.anchorKey))
      this.anchorKey = undefined;
    let changed = false;
    for (const key of this.selected.keys()) {
      if (!availableKeys.has(key)) {
        this.selected.delete(key);
        changed = true;
      }
    }
    if (changed) this.publish();
  }

  clear(): void {
    this.anchorKey = undefined;
    this.selected.clear();
    this.publish();
  }

  setBusy(busy: boolean): void {
    this.busySubject.next(busy);
  }

  private publish(): void {
    this.selectionSubject.next(this.selection);
    this.selectionModeSubject.next(this.selected.size > 0);
  }

  private toSelectionItem(work: WorksModel): WorkSelectionItem {
    const reference: WorkSelectionReference = {
      _id: work._id,
      type: work.type,
    };
    return {
      key: this.getKey(work),
      work,
      reference,
      recommendationIds: (work.recommendations ?? []).map(
        (recommendation) => recommendation._id
      ),
    };
  }

  private getKey(work: WorksModel): string {
    return `${work.type}:${work._id}`;
  }
}
